/* eslint-disable react/forbid-prop-types */
// prop-type is a library for typechecking of props
import { useMemo } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MuiPhoneNumber from "material-ui-phone-number";

// NewUser page components
import FormField from "components/FormField";
import { InputLabel, Switch, Grid, Autocomplete } from "@mui/material";
import MDTypography from "components/MDTypography";
import { ErrorMessage } from "formik";
import MDInput from "components/MDInput";

function NumberForm({ formData, locations, timezones }) {
  const { formField, values, errors, touched, setFieldValue } = formData;
  const { number, description, project, status, location, timezone } = formField;
  const {
    number: numberV,
    description: descriptionV,
    location: locationV,
    timezone: timezoneV,
    project: projectV,
    status: statusV,
  } = values;

  const locationValueOptionFormat = useMemo(() => {
    const lc = locations.find((locationItr) => locationItr.id === locationV);
    if (lc) {
      return {
        id: lc.id,
        label: lc.name,
      };
    }
    return null;
  }, [locations, locationV]);

  const timezoneValueOptionFormat = useMemo(() => {
    const tz = timezones.find((timezoneItr) => timezoneItr.id === timezoneV);
    if (tz) {
      return {
        id: tz.id,
        label: tz.text,
      };
    }
    return null;
  }, [timezones, timezoneV]);

  const statusValue = useMemo(() => {
    switch (typeof statusV === "string") {
      case "string": {
        return statusV === "1";
      }
      case "number": {
        return statusV === 1;
      }
      case "boolean": {
        return statusV;
      }
      default: {
        return !!statusV;
      }
    }
  }, [statusV]);

  const locationOptions = useMemo(
    () =>
      locations.map((l) => ({
        label: l.name,
        id: l.id,
      })),
    [locations]
  );

  const timezoneOptions = useMemo(
    () =>
      timezones.map((l) => ({
        label: l.text,
        id: l.id,
      })),
    [timezones]
  );

  return (
    <MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <MuiPhoneNumber
            label={number.label}
            name={number.name}
            variant="outlined"
            fullWidth
            defaultCountry="us"
            value={numberV}
            error={!!errors.number}
            success={numberV && numberV.length > 0 && !errors.number}
            onChange={(phone) => {
              setFieldValue(number.name, phone);
            }}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage name={number.name} />
            </MDTypography>
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <FormField
            type={description.type}
            label={description.label}
            name={description.name}
            value={descriptionV}
            placeholder={description.placeholder}
            error={errors.description && touched.description}
            success={descriptionV && descriptionV.length > 0 && !errors.description}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            value={locationValueOptionFormat}
            options={locationOptions}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) => setFieldValue(location.name, value?.id, true)}
            isOptionEqualToValue={(option, value) => option?.id === value}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={location.label}
                name={location.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.location && touched.location}
                success={locationV && locationV.length > 0 && !errors.location}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={location.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            value={timezoneValueOptionFormat}
            options={timezoneOptions}
            getOptionLabel={(option) => (option ? option.label : "")}
            onChange={(e, value) => setFieldValue(timezone.name, value?.id, true)}
            isOptionEqualToValue={(option, value) => option.id === value}
            renderInput={(params) => (
              <MDInput
                {...params}
                label={timezone.label}
                name={timezone.name}
                variant="outlined"
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={errors.timezone && touched.timezone}
                success={timezoneV && timezoneV.length > 0 && !errors.timezone}
              />
            )}
          />
          <MDBox mt={0.75}>
            <MDTypography component="div" variant="caption" color="error" fontWeight="regular">
              <ErrorMessage
                name={timezone.name}
                render={(message) => {
                  if (typeof message === "object" && message !== null) {
                    return Object.values(message);
                  }
                  return message;
                }}
              />
            </MDTypography>
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <FormField
            type={project.type}
            label={project.label}
            name={project.name}
            value={projectV}
            placeholder={project.placeholder}
            error={errors.project && touched.project}
            success={projectV && projectV.length > 0 && !errors.project}
            inputProps={{ autoComplete: "" }}
          />
        </Grid>

        <Grid item xs={12}>
          <InputLabel>{status.label}</InputLabel>
          <Switch checked={statusValue} onChange={() => setFieldValue(status.name, !statusValue)} />
        </Grid>
      </Grid>
    </MDBox>
  );
}

// typechecking props for NumberForm
NumberForm.propTypes = {
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).isRequired,
  locations: PropTypes.arrayOf(PropTypes.object).isRequired,
  timezones: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default NumberForm;
